import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  BridgeError,
  type BookingBridge,
  type BookingCommand,
  type BookingProjection,
  type CheckoutSession,
  type FlowStepId,
  type HotelBootstrap,
  type RevisionConflict,
} from "../bridge/contract";
import { bookingProjectionSchema, hotelBootstrapSchema } from "../bridge/schemas";
import { applyHotelBranding } from "../theme/branding";
import { firstUnresolvedStep } from "../flow/FlowMachine";

/**
 * Holds the one piece of state the widget is allowed to have: the server's latest projection.
 *
 * Every mutation goes through `send`, and every successful mutation *replaces* the projection
 * wholesale. There is no merging, no optimistic patching and no local recomputation — if the
 * server drops a room because capacity no longer works, the guest sees that immediately rather
 * than a screen that disagrees with the price.
 */

type FlowContextValue = {
  bootstrap: HotelBootstrap;
  projection: BookingProjection;
  step: FlowStepId;
  visited: ReadonlySet<FlowStepId>;
  goTo: (step: FlowStepId) => void;
  send: (command: BookingCommand) => Promise<void>;
  requestQuote: () => Promise<void>;
  startCheckout: () => Promise<void>;
  refreshProjection: () => Promise<BookingProjection | null>;
  checkoutSession: CheckoutSession | null;
  pending: boolean;
  conflict: RevisionConflict | null;
  dismissConflict: () => void;
  error: string | null;
  dismissError: () => void;
};

const FlowContext = createContext<FlowContextValue | null>(null);

export function useBookingFlow(): FlowContextValue {
  const value = useContext(FlowContext);
  if (!value) throw new Error("useBookingFlow outside of BookingFlowProvider");
  return value;
}

const FLOW_STORAGE_KEY = "exodus-booking-flow";

type ProviderProps = {
  bridge: BookingBridge;
  publicKey: string;
  launchOrigin: string;
  language: string;
  children: ReactNode;
  onReady?: () => void;
};

type LoadState =
  | { phase: "loading" }
  | { phase: "failed"; message: string }
  | { phase: "ready"; bootstrap: HotelBootstrap; projection: BookingProjection };

export function BookingFlowProvider({ bridge, publicKey, launchOrigin, language, children, onReady }: ProviderProps) {
  const [load, setLoad] = useState<LoadState>({ phase: "loading" });
  const [step, setStepState] = useState<FlowStepId>("stay");
  // Which steps the guest has actually seen. Drives whether an unresolved step is allowed to
  // look like an error yet.
  const [visited, setVisited] = useState<ReadonlySet<FlowStepId>>(() => new Set<FlowStepId>(["stay"]));

  const setStep = useCallback((next: FlowStepId) => {
    setStepState(next);
    setVisited((current) => (current.has(next) ? current : new Set([...current, next])));
  }, []);
  const [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState<RevisionConflict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSession | null>(null);
  // Guards against a slow response from an earlier command overwriting a newer projection.
  const revisionSeen = useRef(-1);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        const rawBootstrap = await bridge.bootstrap(publicKey, launchOrigin);
        const bootstrap = hotelBootstrapSchema.parse(rawBootstrap) as HotelBootstrap;

        const savedFlowId = sessionStorage.getItem(FLOW_STORAGE_KEY);
        let projection: BookingProjection | null = null;
        if (savedFlowId) {
          // Resume after a reload. A flow the server no longer knows is not an error worth
          // showing the guest — we quietly start a new one.
          try {
            projection = bookingProjectionSchema.parse(await bridge.getFlow(savedFlowId)) as BookingProjection;
          } catch {
            sessionStorage.removeItem(FLOW_STORAGE_KEY);
          }
        }
        if (!projection) {
          projection = bookingProjectionSchema.parse(
            await bridge.createFlow({ language, channel: "widget" }),
          ) as BookingProjection;
          sessionStorage.setItem(FLOW_STORAGE_KEY, projection.flowId);
        }
        if (cancelled) return;
        revisionSeen.current = projection.revision;
        setLoad({ phase: "ready", bootstrap, projection });
        setStep(projection.revision === 0 ? "stay" : firstUnresolvedStep(bootstrap, projection));
        onReady?.();
      } catch (cause) {
        if (cancelled) return;
        const message =
          cause instanceof BridgeError
            ? cause.message
            : "Die Buchung kann gerade nicht geladen werden. Bitte versuchen Sie es erneut.";
        setLoad({ phase: "failed", message });
      }
    }
    void start();
    return () => {
      cancelled = true;
    };
  }, [bridge, publicKey, launchOrigin, language, onReady, setStep]);

  const adopt = useCallback((next: BookingProjection) => {
    if (next.revision < revisionSeen.current) return;
    revisionSeen.current = next.revision;
    setLoad((current) => (current.phase === "ready" ? { ...current, projection: next } : current));
  }, []);

  const send = useCallback(
    async (command: BookingCommand) => {
      if (load.phase !== "ready") return;
      setPending(true);
      setError(null);
      try {
        const result = await bridge.applyCommand(
          load.projection.flowId,
          load.projection.revision,
          command,
          crypto.randomUUID(),
        );
        if (result.kind === "revision_conflict") {
          setConflict(result);
          adopt(bookingProjectionSchema.parse(result.current) as BookingProjection);
          return;
        }
        adopt(bookingProjectionSchema.parse(result.projection) as BookingProjection);
      } catch (cause) {
        setError(
          cause instanceof BridgeError ? cause.message : "Das hat nicht geklappt. Bitte versuchen Sie es erneut.",
        );
      } finally {
        setPending(false);
      }
    },
    [bridge, load, adopt],
  );

  const requestQuote = useCallback(async () => {
    if (load.phase !== "ready") return;
    setPending(true);
    setError(null);
    try {
      const result = await bridge.requestQuote(load.projection.flowId, load.projection.revision);
      if (result.kind === "revision_conflict") {
        setConflict(result);
        adopt(bookingProjectionSchema.parse(result.current) as BookingProjection);
        return;
      }
      adopt(bookingProjectionSchema.parse(result.projection) as BookingProjection);
    } catch (cause) {
      setError(
        cause instanceof BridgeError ? cause.message : "Der Preis konnte nicht ermittelt werden.",
      );
    } finally {
      setPending(false);
    }
  }, [bridge, load, adopt]);

  const startCheckout = useCallback(async () => {
    if (load.phase !== "ready" || !load.projection.quote) return;
    setPending(true);
    setError(null);
    try {
      const session = await bridge.createCheckoutSession(
        load.projection.flowId,
        load.projection.quote.quoteId,
      );
      setCheckoutSession(session);
      const next = bookingProjectionSchema.parse(
        await bridge.getFlow(load.projection.flowId),
      ) as BookingProjection;
      adopt(next);
    } catch (cause) {
      setError(
        cause instanceof BridgeError ? cause.message : "Die Zahlung konnte nicht gestartet werden.",
      );
    } finally {
      setPending(false);
    }
  }, [bridge, load, adopt]);

  const refreshProjection = useCallback(async (): Promise<BookingProjection | null> => {
    if (load.phase !== "ready") return null;
    try {
      const next = bookingProjectionSchema.parse(
        await bridge.getFlow(load.projection.flowId),
      ) as BookingProjection;
      adopt(next);
      return next;
    } catch (cause) {
      setError(
        cause instanceof BridgeError ? cause.message : "Der Zahlungsstatus konnte nicht aktualisiert werden.",
      );
      return null;
    }
  }, [bridge, load, adopt]);

  const value = useMemo<FlowContextValue | null>(() => {
    if (load.phase !== "ready") return null;
    return {
      bootstrap: load.bootstrap,
      projection: load.projection,
      step,
      visited,
      goTo: setStep,
      send,
      requestQuote,
      startCheckout,
      refreshProjection,
      checkoutSession,
      pending,
      conflict,
      dismissConflict: () => setConflict(null),
      error,
      dismissError: () => setError(null),
    };
  }, [load, step, visited, setStep, send, requestQuote, startCheckout, refreshProjection, checkoutSession, pending, conflict, error]);

  useEffect(() => {
    if (load.phase !== "ready") return;
    applyHotelBranding(load.bootstrap.hotel.branding, document.documentElement);
  }, [load]);

  if (load.phase === "loading") {
    return (
      <div className="booking-boot" role="status" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <p>Verfügbarkeit wird geladen…</p>
      </div>
    );
  }

  if (load.phase === "failed") {
    return (
      <div className="booking-boot booking-boot--error" role="alert">
        <p>{load.message}</p>
      </div>
    );
  }

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}
